import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, Box, Typography, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../../theme";

// Reusable celebratory success modal. Extracted from the admin-registration
// success screen so any surface can drop it in for a big "yes, that worked"
// moment — anything more consequential than a notistack toast deserves a
// slot here (create-invite, complete-onboarding, approve major change, etc.).
//
// notistack still owns short-lived, low-friction "action succeeded" feedback.
// This modal exists for the flows where you want the operator to *stop*, see
// what happened, and choose the next step.

const useStyles = makeStyles(() => ({
  "@keyframes popIn": {
    "0%": { transform: "scale(0.4)", opacity: 0 },
    "70%": { transform: "scale(1.1)", opacity: 1 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },
  "@keyframes fadeUp": {
    "0%": { transform: "translateY(18px)", opacity: 0 },
    "100%": { transform: "translateY(0)", opacity: 1 },
  },
  "@keyframes ringPulse": {
    "0%": { transform: "scale(0.8)", opacity: 0.6 },
    "50%": { transform: "scale(1.25)", opacity: 0.15 },
    "100%": { transform: "scale(1.5)", opacity: 0 },
  },
  "@keyframes confettiFall": {
    "0%": { transform: "translateY(-10px) rotate(0deg)", opacity: 1 },
    "100%": { transform: "translateY(80px) rotate(360deg)", opacity: 0 },
  },
  "@keyframes tickDraw": {
    "0%": { strokeDashoffset: 60 },
    "100%": { strokeDashoffset: 0 },
  },

  dialogPaper: {
    borderRadius: 24,
    boxShadow: "0 24px 64px rgba(21,14,74,0.18)",
    maxWidth: 400,
    width: "100%",
    margin: 16,
    overflow: "hidden",
    background: theme.colors.White,
  },
  dialogContent: {
    padding: 0,
    overflow: "hidden",
    "&:first-child": { paddingTop: 0 },
  },

  confettiWrap: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    overflow: "hidden",
  },
  confettiDot: {
    position: "absolute",
    borderRadius: 2,
    animationFillMode: "both",
    animationTimingFunction: "ease-in",
    animationName: "$confettiFall",
  },

  illustrationArea: {
    position: "relative",
    background: "linear-gradient(160deg, #ECF3FF 0%, #F8FAFF 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 32px 36px",
    overflow: "hidden",
  },
  ringOuter: {
    position: "absolute",
    width: 140,
    height: 140,
    borderRadius: "50%",
    border: `2px solid ${theme.colors.DodgerBlue}`,
    animation: `$ringPulse 1.8s ease-out 0.3s infinite`,
  },
  ringInner: {
    position: "absolute",
    width: 110,
    height: 110,
    borderRadius: "50%",
    border: `2px solid ${theme.colors.DodgerBlue}`,
    animation: `$ringPulse 1.8s ease-out 0.6s infinite`,
  },
  iconCircle: {
    position: "relative",
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${theme.colors.DodgerBlue} 0%, #6FA8FF 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 24px rgba(62,131,251,0.4)",
    animation: `$popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.1s both`,
    zIndex: 1,
  },
  checkSvg: { width: 36, height: 36 },
  checkPath: {
    strokeDasharray: 60,
    strokeDashoffset: 60,
    animation: `$tickDraw 0.4s ease-out 0.55s forwards`,
  },

  body: {
    padding: "28px 32px 32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  title: {
    fontSize: 20,
    fontFamily: theme.fontFamily.MARKPROBLACK,
    color: theme.colors.Violet,
    lineHeight: 1.3,
    animation: `$fadeUp 0.4s ease-out 0.7s both`,
  },
  subtitle: {
    fontSize: 13.5,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    marginTop: 8,
    lineHeight: 1.6,
    maxWidth: 300,
    animation: `$fadeUp 0.4s ease-out 0.85s both`,
  },

  detailRow: {
    display: "flex",
    gap: 10,
    marginTop: 20,
    flexWrap: "wrap",
    justifyContent: "center",
    animation: `$fadeUp 0.4s ease-out 0.95s both`,
  },
  pill: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#ECF3FF",
    border: `1.5px solid ${theme.colors.Periwinkle}`,
    borderRadius: 20,
    padding: "5px 12px",
  },
  pillDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: theme.colors.DodgerBlue,
    flexShrink: 0,
  },
  pillText: {
    fontSize: 12,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
  },

  divider: {
    width: "100%",
    height: 1,
    background: "#ECF3FF",
    marginTop: 24,
    animation: `$fadeUp 0.4s ease-out 1s both`,
  },

  footer: {
    display: "flex",
    gap: 10,
    marginTop: 20,
    width: "100%",
    justifyContent: "center",
    animation: `$fadeUp 0.4s ease-out 1.05s both`,
  },
  btnSecondary: {
    background: theme.colors.Porcelain,
    color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 13,
    textTransform: "none",
    borderRadius: 10,
    padding: "9px 20px",
    border: `1.5px solid ${theme.colors.Gallery}`,
    flex: 1,
    "&:hover": { background: theme.colors.Gallery, color: theme.colors.Violet },
  },
  btnPrimary: {
    background: theme.colors.DodgerBlue,
    color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 13,
    textTransform: "none",
    borderRadius: 10,
    padding: "9px 20px",
    boxShadow: "0 2px 10px rgba(62,131,251,0.25)",
    flex: 1,
    "&:hover": {
      background: "#2A6FE6",
      boxShadow: "0 4px 14px rgba(62,131,251,0.35)",
    },
  },
}));

// Ten confetti bits with predetermined positions — hand-placed rather than
// random so the celebration looks consistent across renders.
const CONFETTI = [
  { left: "15%", top: "10%", w: 8, h: 8, color: "#3E83FB", delay: "0.1s", dur: "1.2s", rotate: 20 },
  { left: "30%", top: "5%", w: 6, h: 10, color: "#78F49E", delay: "0.2s", dur: "1.0s", rotate: -15 },
  { left: "50%", top: "8%", w: 10, h: 6, color: "#C5DAFE", delay: "0.0s", dur: "1.3s", rotate: 45 },
  { left: "68%", top: "6%", w: 7, h: 7, color: "#3E83FB", delay: "0.3s", dur: "1.1s", rotate: -30 },
  { left: "80%", top: "12%", w: 6, h: 9, color: "#78F49E", delay: "0.15s", dur: "1.4s", rotate: 60 },
  { left: "20%", top: "20%", w: 5, h: 5, color: "#C5DAFE", delay: "0.25s", dur: "1.0s", rotate: -45 },
  { left: "75%", top: "20%", w: 8, h: 5, color: "#3E83FB", delay: "0.05s", dur: "1.2s", rotate: 15 },
  { left: "88%", top: "3%", w: 6, h: 6, color: "#78F49E", delay: "0.35s", dur: "1.1s", rotate: 90 },
  { left: "8%", top: "3%", w: 9, h: 5, color: "#3E83FB", delay: "0.4s", dur: "1.3s", rotate: -60 },
  { left: "55%", top: "15%", w: 5, h: 8, color: "#C5DAFE", delay: "0.1s", dur: "0.9s", rotate: 30 },
];

/**
 * Reusable celebratory success alert.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void — called when the user dismisses (backdrop or Close)
 * - title: string — headline; default "Success!"
 * - description: string — supporting copy; hidden if empty
 * - pills: Array<{ label: string, dotColor?: string }> — optional badge row
 * - primaryLabel: string — CTA text; default "Done"
 * - onPrimaryClick: () => void — CTA handler; defaults to onClose
 * - secondaryLabel: string — dismiss text; default "Close"
 * - onSecondaryClick: () => void — dismiss handler; defaults to onClose
 * - hideSecondary: boolean — one-button mode
 * - hideConfetti: boolean — softer variant without the animation
 */
export default function SuccessAlert({
  open,
  onClose,
  title = "Success!",
  description = "",
  pills = [],
  primaryLabel = "Done",
  onPrimaryClick,
  secondaryLabel = "Close",
  onSecondaryClick,
  hideSecondary = false,
  hideConfetti = false,
}) {
  const classes = useStyles();
  const [show, setShow] = useState(false);

  // Delay the confetti/pulse a tick after mount so the CSS animations get a
  // clean starting state — otherwise the first-frame flash is visible on some
  // browsers.
  useEffect(() => {
    let timer;
    if (open) {
      timer = setTimeout(() => setShow(true), 50);
    } else {
      setShow(false);
    }
    return () => clearTimeout(timer);
  }, [open]);

  const validPills = (pills || []).filter((p) => p && p.label);

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.illustrationArea}>
          {show && !hideConfetti && (
            <Box className={classes.confettiWrap}>
              {CONFETTI.map((c, i) => (
                <Box
                  key={i}
                  className={classes.confettiDot}
                  style={{
                    left: c.left,
                    top: c.top,
                    width: c.w,
                    height: c.h,
                    background: c.color,
                    transform: `rotate(${c.rotate}deg)`,
                    animationDuration: c.dur,
                    animationDelay: c.delay,
                  }}
                />
              ))}
            </Box>
          )}

          {show && (
            <>
              <Box className={classes.ringOuter} />
              <Box className={classes.ringInner} />
            </>
          )}

          <Box className={classes.iconCircle}>
            <svg className={classes.checkSvg} viewBox="0 0 36 36" fill="none">
              <path
                className={classes.checkPath}
                d="M8 18 L15 25 L28 11"
                stroke="white"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        </Box>

        <Box className={classes.body}>
          <Typography className={classes.title}>{title}</Typography>
          {description ? (
            <Typography className={classes.subtitle}>{description}</Typography>
          ) : null}

          {validPills.length > 0 && (
            <Box className={classes.detailRow}>
              {validPills.map((p, i) => (
                <Box key={i} className={classes.pill}>
                  <Box
                    className={classes.pillDot}
                    style={p.dotColor ? { background: p.dotColor } : undefined}
                  />
                  <Typography className={classes.pillText}>{p.label}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box className={classes.divider} />

          <Box className={classes.footer}>
            {!hideSecondary && (
              <Button
                className={classes.btnSecondary}
                onClick={onSecondaryClick || onClose}
              >
                {secondaryLabel}
              </Button>
            )}
            <Button
              className={classes.btnPrimary}
              onClick={onPrimaryClick || onClose}
            >
              {primaryLabel}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
